import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { handleUnauthorizedResponse } from '../utils/sessionReplacedHandler'
import * as authService from './authService'

function getTenantId(): string {
  const user = authService.getCurrentUser()
  return user?.tenantId || getRuntimeTenantIdSync() || ''
}

async function cashbackFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = authService.getAccessToken()
  const tenantId = getTenantId()
  const response = await fetch(withApiBaseUrl(endpoint), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      ...(options?.headers as Record<string, string>),
    },
    credentials: 'include',
  })

  const data = await response.json().catch(() => ({ success: false, message: 'Invalid cashback response' }))
  if (await handleUnauthorizedResponse(response.status, data)) {
    throw new Error((data as { code?: string })?.code === 'SESSION_REPLACED' ? 'SESSION_REPLACED' : 'AUTH_EXPIRED')
  }
  if (!response.ok) {
    throw new Error((data as any)?.message || `HTTP ${response.status}`)
  }
  return data as T
}

export type MemberCashbackSettlementStatus =
  | 'claimable'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'paid'
  | 'payment_failed'
  | 'expired'

export interface MemberCashbackSettlement {
  _id: string
  periodKey: string
  periodStartAt: string
  periodEndAt: string
  status: MemberCashbackSettlementStatus
  calculation?: {
    cashbackRate?: number
    cashbackAmount?: number
  }
  sourceSummary?: {
    netLoss?: number
    validTurnover?: number
  }
  bonusLock?: {
    turnoverMultiplier?: number
    turnoverRequired?: number
    withdrawPolicy?: string
  }
  claim?: {
    claimedAt?: string | null
  }
  payout?: {
    paidAt?: string | null
  }
}

export interface MemberCashbackSummary {
  claimableAmount: number
  pendingAmount: number
  approvedAmount: number
  paidAmount: number
  totalEarned: number
}

export async function getCashbackMe(): Promise<{
  success: boolean
  data: {
    summary: MemberCashbackSummary
    latestClaimable: MemberCashbackSettlement | null
    latestPaid: MemberCashbackSettlement | null
  }
}> {
  return cashbackFetch('/api/v1/user-auth/cashback/me')
}

export async function getCashbackHistory(limit = 20): Promise<{
  success: boolean
  data: MemberCashbackSettlement[]
}> {
  return cashbackFetch(`/api/v1/user-auth/cashback/history?limit=${Math.min(Math.max(limit, 1), 100)}`)
}

export async function claimCashbackSettlement(settlementId: string, note = ''): Promise<{
  success: boolean
  data?: MemberCashbackSettlement
  message?: string
}> {
  return cashbackFetch(`/api/v1/user-auth/cashback/${encodeURIComponent(settlementId)}/claim`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })
}
